import type {
  EFormRenderResponse,
  EFormSubmitAnswer,
  EFormSubmitPayload,
  EFormSubmitResponse,
  UrlResolveResponse,
} from './eform.types'

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '/api/v5'

/**
 * The response body as sent, with the error mapping every caller relies on but
 * no envelope assumption. Split out because /eforms/upload-presign answers with
 * a bare { uploadUrl, s3Key } while every other eform route returns { data }.
 */
async function requestJson(url: string, options?: RequestInit): Promise<Record<string, unknown>> {
  const res = await fetch(url, options)
  const json = await res.json()

  if (!res.ok) {
    const errorMsg = json?.error?.message || `Request failed (${res.status})`
    const errorCode = json?.error?.code || 'ERROR'
    const err = new Error(errorMsg) as Error & { code: string; status: number }
    err.code = errorCode
    err.status = res.status
    throw err
  }

  return json
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  return (await requestJson(url, options)).data as T
}

export const eformService = {
  /** `params` are the page's own query parameters (all but `t`), sent as params[key]. */
  loadForm(token: string, params: Record<string, string> = {}): Promise<EFormRenderResponse> {
    const query = new URLSearchParams({ t: token })
    for (const [key, value] of Object.entries(params)) query.append(`params[${key}]`, value)
    return request<EFormRenderResponse>(`${API_BASE}/eforms/render?${query.toString()}`)
  },

  /** The public download URL of a submit's PDF copy (the thank-you page's link). */
  pdfDownloadUrl(handle: string): string {
    return `${API_BASE}/eforms/pdf?d=${encodeURIComponent(handle)}`
  },

  submitForm(token: string, payload: EFormSubmitPayload): Promise<EFormSubmitResponse> {
    return request<EFormSubmitResponse>(
      `${API_BASE}/eforms/submit?t=${encodeURIComponent(token)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    )
  },

  /**
   * One answer of a visit with partial saves on (eformAutosave). Run through the
   * visit queue (eformQueue) so `visitToken` is the latest one; the response
   * carries the refreshed token naming the visit's response.
   */
  saveAnswer(token: string, answer: EFormSubmitAnswer & { visitToken: string }): Promise<{ responseId: number; visitToken: string }> {
    return request<{ responseId: number; visitToken: string }>(
      `${API_BASE}/eforms/answer?t=${encodeURIComponent(token)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answer),
      }
    )
  },

  createPaymentIntent(token: string, merchantId: number): Promise<{ clientSecret: string; intentId: string; publicApiKey: string }> {
    return request<{ clientSecret: string; intentId: string; publicApiKey: string }>(
      `${API_BASE}/eforms/payment-intent?t=${encodeURIComponent(token)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchantId }),
      }
    )
  },

  /**
   * Run through the visit queue (eformQueue) so `visitToken` is the latest one;
   * the response carries the refreshed token naming the visit's response.
   */
  async uploadFile(
    token: string,
    file: File,
    visitToken?: string
  ): Promise<{ filename: string; responseId?: number; visitToken?: string }> {
    const formData = new FormData()
    if (visitToken) formData.append('visitToken', visitToken)

    // Files over 4 MB can't ride through the Lambda (6 MB payload cap), so
    // presign a direct-to-S3 PUT (token-gated, no auth) and post only the
    // s3Key. Smaller files go multipart as before.
    const SIZE_THRESHOLD = 4 * 1024 * 1024
    if (file.size > SIZE_THRESHOLD) {
      const presignBody = await requestJson(
        `${API_BASE}/eforms/upload-presign?t=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
          }),
        }
      )

      // This route answers flat, so the enveloped read used everywhere else gave
      // undefined and threw on the property access below. Accept either shape,
      // and refuse to PUT without both fields: fetch(undefined) resolves against
      // the app origin and returns index.html with a 200, which would upload
      // nothing and then post "undefined" as the s3Key.
      const presign = ((presignBody?.data ?? presignBody) ?? {}) as {
        uploadUrl?: string
        s3Key?: string
      }
      if (!presign.uploadUrl || !presign.s3Key) {
        const err = new Error(`Could not start the upload for ${file.name}.`) as Error & {
          code: string
          status: number
        }
        err.code = 'UPLOAD_ERROR'
        err.status = 0
        throw err
      }

      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      })
      if (!putRes.ok) {
        const err = new Error(`Upload failed (${putRes.status})`) as Error & { code: string; status: number }
        err.code = 'UPLOAD_ERROR'
        err.status = putRes.status
        throw err
      }

      formData.append('s3Key', presign.s3Key)
    } else {
      formData.append('file', file)
    }

    const res = await fetch(
      `${API_BASE}/eforms/upload?t=${encodeURIComponent(token)}`,
      { method: 'POST', body: formData }
    )
    const json = await res.json()

    if (!res.ok) {
      const errorMsg = json?.error?.message || `Upload failed (${res.status})`
      const err = new Error(errorMsg) as Error & { code: string; status: number }
      err.code = json?.error?.code || 'ERROR'
      err.status = res.status
      throw err
    }

    return json.data as { filename: string; responseId?: number; visitToken?: string }
  },

  resolveShortUrl(code: string): Promise<UrlResolveResponse> {
    return request<UrlResolveResponse>(
      `${API_BASE}/url/resolve?t=${encodeURIComponent(code)}`
    )
  },
}
